<script setup>
import { useThemeConfig } from '@core/composable/useThemeConfig'
import {useUserGroupsStore, useUsersStore, useReservationsStore, useRestaurantsStore, useCmsContentStore, usePreferredStatusStore, useAppModeStore} from '@/stores'
import {storeToRefs} from "pinia"
import {useRoute, useRouter} from "vue-router"
import LoginJoinDialog from "@core/components/loginJoinDialog.vue"
import RestaurantsInfoDialog from "@core/components/RestaurantsInfoDialog.vue"
import { connectSocket, disconnectSocket } from '@/composables/useSocket'
import { initRealtime, resetRealtime } from '@/composables/useRealtime'
import { initMessaging, resetMessaging } from '@/composables/useMessaging'
import { initSupport, resetSupport } from '@/composables/useSupport'
import { markActivity, resetGreeting } from '@/utils/launcherSession'

const usersStore = useUsersStore()
const cmsStore = useCmsContentStore()
const appModeStore = useAppModeStore()

let myUser = null
let canActForMembers = null
let isShowingRestaurantsInfoDialog = null;
({ myUser, canActForMembers, isShowingRestaurantsInfoDialog } = storeToRefs(usersStore))
const { showCmsEditFab } = storeToRefs(cmsStore)
const isVerifyEmailWithCodeDialogVisible = ref(false)

// Immediately after creating the store instance, call loadInitialData
const userGroupsStore = useUserGroupsStore()

const reservationsStore = useReservationsStore()
const restaurantsStore = useRestaurantsStore()
const preferredStatusStore = usePreferredStatusStore()

// Track user activity (throttled) so the RM AI launcher can tell an active
// session from a returning-after-idle one. Passive listeners; root component
// stays mounted for the app lifetime.
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart']

onMounted(() => {
  usersStore.loadInitialData()
  userGroupsStore.loadInitialData()
  reservationsStore.loadInitialData()
  restaurantsStore.loadInitialData()

  ACTIVITY_EVENTS.forEach(evt =>
    window.addEventListener(evt, markActivity, { passive: true }),
  )
})

onBeforeUnmount(() => {
  ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, markActivity))
})

// Connect unified socket when user authenticates, disconnect on logout
watch(() => myUser.value?._id, (userId, oldUserId) => {
  if (userId && !oldUserId) {
    connectSocket()
    initRealtime()
    initMessaging()
    initSupport()
    preferredStatusStore.loadPreferredStatus()
    appModeStore.hydrate(userId, myUser.value)
  } else if (!userId && oldUserId) {
    resetRealtime()
    resetMessaging()
    resetSupport()
    disconnectSocket()
    preferredStatusStore.invalidate()
    appModeStore.resetOnLogout()

    // Clear the launcher greeting state so the next login greets fresh.
    resetGreeting()
  }
}, { immediate: true })


const {
  syncInitialLoaderTheme,
  syncVuetifyThemeWithTheme: syncConfigThemeWithVuetifyTheme,
  isAppRtl
} = useThemeConfig()

// ℹ️ Sync current theme with initial loader theme
syncInitialLoaderTheme()
syncConfigThemeWithVuetifyTheme()


//check to see if we should pop up any dialogs when the usersStore changes
//this seems hacky
usersStore.$subscribe((mutation, state) => {
  const emailConfirmed = state?.myUser?.emailConfirmed ? true : false
  if(!emailConfirmed && state?.myUser?.email.includes("@")){
    usersStore.showCodeConfirmationDialog()
  }
  else if(emailConfirmed && !state?.myUser?.memberCommitment?.accepted){
    // Existing-member gate — required acceptance before any further RM use.
    // Phase 3 overrides `source` to 'onboarding' immediately after register/
    // verify-email completes; here we default to existing_member_gate.
    if (!state?.isShowingMemberCommitmentDialog) {
      usersStore.showMemberCommitmentDialog({ mode: 'gate', source: 'existing_member_gate' })
    }
  }
  else if(state?.myUser?.promptToVerify && !state?.myUser?.verified){
    usersStore.showVerificationDialog()
  }
  else if(state?.myUser?.verified){
    usersStore.hideVerificationDialog()
  }
})

// Toast notification when a user is redirected from a guarded route (e.g. /admin/* without eligibility).
const route = useRoute()
const router = useRouter()
const toastVisible = ref(false)
const toastMessage = ref('')

// D21 (Kapil and the product advisor, 9/30): code outside a component — a
// refused act-as start in plugins/misc.js — shows a message in this snackbar
// (window event 'rm-toast', { message }) instead of a browser alert.
const onToastEvent = e => {
  const message = e?.detail?.message
  if (!message) return
  toastMessage.value = message
  toastVisible.value = true
}

onMounted(() => window.addEventListener('rm-toast', onToastEvent))
onBeforeUnmount(() => window.removeEventListener('rm-toast', onToastEvent))

watch(
  () => route.query.reason,
  reason => {
    if (reason === 'not-authorized') {
      toastMessage.value = "You don't have access to Restaurant Admin mode."
      toastVisible.value = true

      // Strip the reason from the URL so reloads don't re-show the toast.
      const { reason: _reason, ...rest } = route.query
      router.replace({ path: route.path, query: rest })
    }
  },
  { immediate: true },
)
</script>

<template>
  <VLocaleProvider :rtl="isAppRtl">
    <VApp>
      <VMain>
        <NoNetworkOverlay />
        <UpdatingAppOverlay />

        <VerificationDialog
          v-if="myUser"
        />

        <VerifyEmailWithCodeDialog
          v-if="myUser"
        />

        <MemberCommitmentDialog
          v-if="myUser"
        />

        <DeleteAccountDialog
          v-if="myUser"
          :is-dialog-visible="usersStore.isShowingDeleteAccountDialog"
          @update:is-dialog-visible="val => (val ? usersStore.showDeleteAccountDialog() : usersStore.hideDeleteAccountDialog())"
        />

        <EditProfileDialog
          v-if="myUser"
        />
        <RestaurantDetailsDialog />
        <PreferredRequestInfoDialog />
        <MembersInfoDialog
          v-model:is-dialog-visible="usersStore.isShowingMembersInfoDialog"
          :initial-tab="usersStore.membersInfoDialogTab"
        />

        <EditSecondaryMemberDialog
          v-if="myUser?.activeSubscription && myUser?.isPlatinum"
        />

        <EditPersonalAdminsDialog
          v-if="myUser?.activeSubscription && myUser?.isPlatinum"
        />

        <EditReservationDialog />
        <ViewReservationDialog v-if="myUser" />
        <ChooseMemberForRequestDialog v-if="myUser" />

        <CancelReservationDialog />

        <AdminEditRestaurantDialog v-if="myUser?.userLevel == 'admin' " />
        <RestaurantEditRestaurantDialog v-if="myUser?.userLevel == 'admin' || myUser?.primaryGroupPermission == 'owner' || myUser?.primaryGroupPermission == 'admin'" />

        <AdminEditUserGroupDialog v-if="myUser?.userLevel == 'admin' || myUser?.primaryGroupPermission == 'owner' || myUser?.primaryGroupPermission == 'admin'" />
        <AdminEditUserGroupMembersDialog v-if="myUser?.userLevel == 'admin' || myUser?.primaryGroupPermission == 'owner' || myUser?.primaryGroupPermission == 'admin' || myUser?.primaryGroupPermission == 'adminStandard'" />
        <AdminEditUserGroupSubscriptionDialog v-if="myUser?.userLevel == 'admin' || myUser?.primaryGroupPermission == 'owner' || myUser?.primaryGroupPermission == 'admin'" />
        <AdminEditGlobalSettingsDialog v-if="myUser?.userLevel == 'admin'" />
        <AdminDirectReservationDialog v-if="myUser?.userLevel == 'admin' || canActForMembers.length > 0" />
        <CmsEditToggleButton v-if="myUser?.userLevel == 'admin' && showCmsEditFab" />
        <CmsInlineEditDialog v-if="myUser?.userLevel == 'admin'" />
        <!-- AgentChat moved to /ai page route -->
        <LoginJoinDialog />
        <!--
          Global For Restaurants modal — card Preferred seals and the
          Preferred tab descriptor open it at the Benefits tab 
        -->
        <RestaurantsInfoDialog
          v-model:is-dialog-visible="isShowingRestaurantsInfoDialog"
          initial-tab="preferred-benefits"
        />
        <RouterView :key="$route.fullPath" />
        <LoadingOverlay />
        <VSnackbar
          v-model="toastVisible"
          :timeout="4000"
          location="top"
          color="warning"
        >
          {{ toastMessage }}
          <template #actions>
            <VBtn
              variant="text"
              @click="toastVisible = false"
            >
              Dismiss
            </VBtn>
          </template>
        </VSnackbar>
      </VMain>
    </VApp>
  </VLocaleProvider>
</template>
